import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App, type PluginListenerHandle } from '@capacitor/app';

/** Routes racines de la bottom nav : retour = minimiser l'app. */
const ROOT_ROUTES = new Set([
  '/',
  '/pros',
  '/messaging',
  '/profile',
  '/favorites',
  '/recherche',
]);

/**
 * Gestion du bouton retour Android pour l'app Capacitor.
 * - Sur une route racine : minimise l'app (ne quitte pas).
 * - Ailleurs : navigate(-1) comme le comportement web standard.
 */
export function useAndroidBackHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;

    let handle: PluginListenerHandle | null = null;
    let cancelled = false;

    void App.addListener('backButton', (data) => {
      if (cancelled) return;

      const isRoot = ROOT_ROUTES.has(pathnameRef.current);

      if (isRoot || !data.canGoBack) {
        void App.minimizeApp().catch(() => App.exitApp());
      } else {
        navigateRef.current(-1);
      }
    }).then((listener) => {
      if (cancelled) {
        void listener.remove();
      } else {
        handle = listener;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, []);
}
